import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  ChevronRight,
  ExternalLink,
  Loader2,
  Pause,
  Rocket,
  X,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  approveWorkflowRun,
  cancelWorkflowRun,
  getWorkflowRun,
  getWorkflowRunByWorker,
  rejectWorkflowRun,
} from '@/lib/api';
import { useWorkflowStore } from '@/stores/workflow-store';
import { formatDurationMs } from '@/lib/format';
import { isTerminalStatus } from '@/lib/workflow-utils';
import type { DagNodeState, WorkflowStepStatus } from '@/lib/types';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { StatusPill, type StatusPillStatus } from '@/components/ui/status-pill';

interface WorkflowProgressCardProps {
  runId: string;
  workflowName: string;
  workerConversationId?: string | null;
}

export function WorkflowProgressCard({
  runId,
  workflowName,
  workerConversationId,
}: WorkflowProgressCardProps): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workflowRunQueryKey = workerConversationId
    ? ['workflowRun', runId, workerConversationId]
    : ['workflowRun', runId];

  // REST polling for run data (stops when terminal)
  const {
    data: runData,
    isError,
    refetch,
  } = useQuery({
    queryKey: workflowRunQueryKey,
    queryFn: () =>
      workerConversationId ? getWorkflowRunByWorker(workerConversationId) : getWorkflowRun(runId),
    refetchInterval: (query): number | false => {
      const status = query.state.data?.run.status;
      if (status === 'completed' || status === 'failed' || status === 'cancelled') return false;
      return 3000;
    },
  });

  const resolvedRunId = runData?.run.id ?? runId;
  const restStatus = runData?.run.status ?? 'pending';

  // Live SSE state from Zustand store
  const liveState = useWorkflowStore(state => state.workflows.get(resolvedRunId));

  // Merge: prefer live state when available
  const status = liveState?.status ?? restStatus;
  const dagNodes: DagNodeState[] = liveState?.dagNodes ?? [];
  const currentTool = liveState?.currentTool ?? null;
  const approval = liveState?.approval ?? null;
  const error = liveState?.error;
  const startedAt = liveState?.startedAt;

  const isRunning = status === 'running' || status === 'pending';
  const isPaused = status === 'paused';

  // Expand/collapse state
  const [expanded, setExpanded] = useState(false);
  const userToggled = useRef(false);

  // Auto-expand when running or paused, auto-collapse when terminal (unless user toggled)
  useEffect(() => {
    if (userToggled.current) return;
    if (isRunning || isPaused) {
      setExpanded(true);
    } else if (isTerminalStatus(status)) {
      setExpanded(false);
    }
  }, [isRunning, isPaused, status]);

  // Live elapsed timer
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!isRunning || !startedAt) return;
    setElapsed(Date.now() - startedAt);
    const interval = setInterval(() => {
      setElapsed(Date.now() - startedAt);
    }, 1000);
    return (): void => {
      clearInterval(interval);
    };
  }, [isRunning, startedAt]);

  // Approve/reject/cancel mutations
  const approveMutation = useMutation({
    mutationFn: () => approveWorkflowRun(resolvedRunId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workflowRunQueryKey });
    },
  });
  const rejectMutation = useMutation({
    mutationFn: () => rejectWorkflowRun(resolvedRunId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workflowRunQueryKey });
    },
  });
  const cancelMutation = useMutation({
    mutationFn: () => cancelWorkflowRun(resolvedRunId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workflowRunQueryKey });
    },
  });
  const mutationError = approveMutation.error ?? rejectMutation.error;

  // Completed duration from live state
  const completedAt = liveState?.completedAt;
  const finalDuration = completedAt && startedAt ? completedAt - startedAt : null;

  const handleHeaderClick = (): void => {
    userToggled.current = true;
    setExpanded(prev => !prev);
  };

  const handleViewFullScreen = (): void => {
    if (resolvedRunId) {
      navigate(`/workflows/runs/${resolvedRunId}`);
    } else {
      navigate(`/chat/${encodeURIComponent(workerConversationId ?? '')}`);
    }
  };

  // Loading state: no run data yet
  if (!runData && !isError) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-xs max-w-md">
        <Loader2 className="size-3.5 animate-spin text-primary shrink-0" />
        <span className="truncate text-text-primary font-medium">{workflowName}</span>
        <span className="text-text-tertiary">Starting…</span>
      </div>
    );
  }

  // Error state: couldn't fetch run
  if (isError && !runData) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-xs max-w-md">
        <span className="text-error text-xs shrink-0">&#x26A0;</span>
        <span className="truncate text-text-primary font-medium">{workflowName}</span>
        <button
          onClick={(): void => {
            refetch();
          }}
          className="text-primary hover:text-accent-bright transition-colors shrink-0"
        >
          Retry
        </button>
      </div>
    );
  }

  const userMessage = runData?.run.user_message ?? null;
  const shortRunId = resolvedRunId.length > 12 ? `${resolvedRunId.slice(0, 12)}…` : resolvedRunId;

  return (
    <div
      className={cn(
        'relative max-w-2xl overflow-hidden rounded-lg border border-flame/30 bg-flame-bg-soft/40 transition-colors',
        isPaused && 'border-warning/30 bg-[oklch(0.78_0.16_75/0.06)]'
      )}
    >
      {/* atmospheric flame glow in top-right corner */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-8 -right-8 size-32 rounded-full opacity-40 blur-2xl"
        style={{ background: 'var(--flame-bg)' }}
      />

      {/* Header — title + status pill + chevron + run id */}
      <button
        type="button"
        onClick={handleHeaderClick}
        className="relative flex w-full items-center gap-2.5 px-3.5 pt-3 pb-1 text-left"
      >
        <Rocket className="size-4 shrink-0 text-flame" />
        <span className="text-sm font-semibold text-text-primary">Workflow dispatched</span>
        <StatusPill status={status as StatusPillStatus} pulse={isRunning} />
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {isRunning && elapsed > 0 ? (
            <span className="font-mono text-[10.5px] text-flame-hi">
              {formatDurationMs(elapsed)}
            </span>
          ) : finalDuration != null ? (
            <span className="font-mono text-[10.5px] text-text-tertiary">
              {formatDurationMs(finalDuration)}
            </span>
          ) : null}
          <span className="font-mono text-[10.5px] text-text-tertiary">{shortRunId}</span>
          <ChevronRight
            className={cn(
              'size-3.5 text-text-tertiary transition-transform duration-150',
              expanded && 'rotate-90'
            )}
          />
        </span>
      </button>

      {expanded && (
        <div className="relative space-y-2.5 px-3.5 pt-2 pb-3">
          {/* Command preview — mirrors the design's `$ metis run ... --message "..."` */}
          {userMessage !== null && (
            <div className="rounded-md border border-border bg-background px-3 py-2 font-mono text-[12.5px] leading-snug">
              <span className="text-flame-hi">$ metis run </span>
              <span className="text-text-primary">{workflowName}</span>
              <span className="text-flame-hi"> --message </span>
              <span className="text-accent-bright">
                &quot;{userMessage.length > 80 ? `${userMessage.slice(0, 80)}…` : userMessage}&quot;
              </span>
            </div>
          )}

          {/* Horizontal progress dots — one per DAG node */}
          {dagNodes.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              {dagNodes.map((node: DagNodeState) => (
                <ProgressDotInline key={node.nodeId} status={node.status} name={node.name} />
              ))}
            </div>
          )}

          {/* Current tool activity */}
          {currentTool?.status === 'running' && (
            <div className="flex items-center gap-2 text-xs">
              <span className="fd-sweep size-3 shrink-0" />
              <span className="truncate font-mono text-accent-bright">{currentTool.name}</span>
            </div>
          )}

          {/* Approval request banner */}
          {isPaused && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 rounded-md border border-warning/25 bg-[oklch(0.78_0.16_75/0.08)] px-3 py-2">
                <Pause className="mt-0.5 size-3.5 shrink-0 text-warning" />
                <p className="text-xs text-warning">
                  {approval?.message ?? 'Waiting for approval'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    approveMutation.mutate();
                  }}
                  disabled={!runId || approveMutation.isPending || rejectMutation.isPending}
                  className="inline-flex items-center gap-1 rounded-md border border-flame/30 bg-flame-bg-soft px-2 py-1 text-xs font-semibold text-flame-hi transition-colors hover:border-flame/50 hover:bg-flame-bg disabled:opacity-50"
                >
                  <CheckCircle className="size-3" />
                  Approve
                </button>
                <ConfirmDialog
                  trigger={
                    <button
                      type="button"
                      disabled={!runId || approveMutation.isPending || rejectMutation.isPending}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-error/80 transition-colors hover:bg-error/10 hover:text-error disabled:opacity-50"
                    >
                      <XCircle className="size-3" />
                      Reject
                    </button>
                  }
                  title="Reject workflow?"
                  description={
                    <>
                      Reject the paused workflow <strong>{workflowName}</strong>. The run will be
                      marked as failed and any pending iterations will not continue.
                    </>
                  }
                  confirmLabel="Reject"
                  onConfirm={(): void => {
                    rejectMutation.mutate();
                  }}
                />
              </div>
              {(approveMutation.isError || rejectMutation.isError) && (
                <p className="text-xs text-error">
                  {mutationError instanceof Error
                    ? mutationError.message
                    : 'Action failed — please try again'}
                </p>
              )}
            </div>
          )}

          {/* Error message */}
          {status === 'failed' && error && (
            <div className="truncate text-xs text-error" title={error}>
              {error.slice(0, 120)}
            </div>
          )}

          {/* Actions — Open run + Cancel (running only) */}
          <div className="flex items-center gap-1.5 pt-0.5">
            <button
              type="button"
              onClick={handleViewFullScreen}
              className="inline-flex items-center gap-1 rounded-md border border-flame/30 bg-flame-bg-soft px-2 py-1 text-xs font-semibold text-flame-hi transition-colors hover:border-flame/50 hover:bg-flame-bg"
            >
              <ExternalLink className="size-3" />
              Open run
            </button>
            {isRunning && (
              <ConfirmDialog
                trigger={
                  <button
                    type="button"
                    disabled={cancelMutation.isPending}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-text-secondary transition-colors hover:bg-surface-elevated hover:text-text-primary disabled:opacity-50"
                  >
                    <X className="size-3" />
                    Cancel
                  </button>
                }
                title="Cancel workflow?"
                description={
                  <>
                    Cancel <strong>{workflowName}</strong>. The run will be marked as cancelled and
                    any in-flight subprocess will be terminated.
                  </>
                }
                confirmLabel="Cancel workflow"
                onConfirm={(): void => {
                  cancelMutation.mutate();
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Horizontal progress dot — colored circle + node name. Matches the design's
 * compact dispatch-card progress row (vs the vertical node list we had). */
function ProgressDotInline({
  status,
  name,
}: {
  status: WorkflowStepStatus;
  name: string;
}): React.ReactElement {
  const cfg = {
    completed: { ring: 'border-success', fill: 'bg-success', text: 'text-text-primary' },
    running: {
      ring: 'border-flame',
      fill: 'bg-transparent shadow-[0_0_8px_var(--flame)]',
      text: 'text-text-primary',
    },
    failed: { ring: 'border-error', fill: 'bg-error', text: 'text-error' },
    pending: { ring: 'border-text-muted', fill: 'bg-transparent', text: 'text-text-tertiary' },
    skipped: { ring: 'border-text-muted', fill: 'bg-transparent', text: 'text-text-muted' },
    cancelled: { ring: 'border-text-muted', fill: 'bg-transparent', text: 'text-text-muted' },
    paused: { ring: 'border-warning', fill: 'bg-warning', text: 'text-text-primary' },
  }[status] ?? {
    ring: 'border-text-muted',
    fill: 'bg-transparent',
    text: 'text-text-tertiary',
  };
  return (
    <div className="inline-flex items-center gap-1.5 text-[11px]">
      <span
        className={cn(
          'size-2 rounded-full border-[1.5px]',
          cfg.ring,
          cfg.fill,
          status === 'running' && 'animate-[dup-blink_1.4s_ease-in-out_infinite]'
        )}
      />
      <span className={cn('font-mono', cfg.text)}>{name}</span>
    </div>
  );
}
